import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Moon, ArrowLeft, Send, Loader2, CheckCircle2, SkipForward } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const FridayDebrief = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const { t } = useTranslation();
  const [debrief, setDebrief] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { startOrResume(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const startOrResume = async () => {
    try {
      const res = await api.post('/api/debrief/start');
      const data = res.data;
      setDebrief(data.debrief);
      setMessages(data.debrief.conversations || [{ role: 'atlas', content: data.atlas_message, step: 1 }]);
      setIsComplete(data.debrief.statut === 'completee');
      if (data.debrief.statut !== 'completee') setTimeout(() => inputRef.current?.focus(), 300);
    } catch (err) {
      if (err.response?.status === 400) {
        try {
          const currentRes = await api.get('/api/debrief/current');
          if (currentRes.data.debrief) {
            setDebrief(currentRes.data.debrief);
            setMessages(currentRes.data.debrief.conversations || []);
            setIsComplete(true);
          }
        } catch (e) { console.error(e); }
      } else {
        console.error('Debrief start error:', err);
      }
    } finally { setLoading(false); }
  };

  const submitStep = async () => {
    if (!input.trim() || sending || !debrief) return;
    const currentStep = debrief.current_step || 1;
    const userMsg = input.trim();
    setInput('');
    setSending(true);
    setMessages(prev => [...prev, { role: 'user', content: userMsg, step: currentStep }]);
    try {
      const res = await api.post('/api/debrief/step', {
        debrief_id: debrief.id,
        step: currentStep,
        response: userMsg
      });
      const data = res.data;
      setDebrief(data.debrief);
      setMessages(prev => [...prev, { role: 'atlas', content: data.atlas_message, step: currentStep }]);
      if (data.is_complete) setIsComplete(true);
      else setTimeout(() => inputRef.current?.focus(), 300);
    } catch (err) {
      toast.error(t('common.error'));
      setMessages(prev => prev.slice(0, -1));
      setInput(userMsg);
    } finally { setSending(false); }
  };

  const handleSkipStep4 = async () => {
    if (sending || !debrief || debrief.current_step !== 4) return;
    setInput('rien à poser');
    setSending(true);
    setMessages(prev => [...prev, { role: 'user', content: t('debrief.nothingToAdd'), step: 4 }]);
    try {
      const res = await api.post('/api/debrief/step', {
        debrief_id: debrief.id,
        step: 4,
        response: 'rien à poser'
      });
      setDebrief(res.data.debrief);
      setMessages(prev => [...prev, { role: 'atlas', content: res.data.atlas_message, step: 4 }]);
      setIsComplete(true);
    } catch (err) { toast.error(t('common.error')); }
    finally { setSending(false); setInput(''); }
  };

  const handleSkip = async () => {
    try {
      await api.put('/api/debrief/skip');
      toast.success(t('debrief.skipped'));
      navigate('/dashboard');
    } catch (err) { toast.error(t('common.error')); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitStep(); }
  };

  const getStepLabel = (step) => {
    const labels = {
      1: t('debrief.steps.victory'),
      2: t('debrief.steps.learning'),
      3: t('debrief.steps.openTopic'),
      4: t('debrief.steps.letGo')
    };
    return labels[step] || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-screen bg-atlasBg">
        <div className="text-atlasPurple animate-pulse">{t('common.loading')}</div>
      </div>
    );
  }

  const currentStep = debrief?.current_step || 1;

  return (
    <div data-testid="friday-debrief-page" className="flex flex-col h-screen bg-atlasBg">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-atlasBorder bg-atlasCard/80 backdrop-blur-lg">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/dashboard')} data-testid="debrief-back-btn" className="p-2 rounded-lg text-atlasTextSecondary hover:text-white hover:bg-white/5 transition-all">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <Moon className="w-5 h-5 text-atlasPurple" />
                <h1 className="text-lg font-semibold text-white">{t('debrief.title')}</h1>
              </div>
            </div>
            {!isComplete && (
              <button onClick={handleSkip} data-testid="debrief-skip-btn" className="flex items-center gap-1.5 text-sm text-atlasTextSecondary hover:text-white transition-all px-3 py-1.5 rounded-lg hover:bg-white/5">
                <SkipForward className="w-4 h-4" />
                {t('debrief.skip')}
              </button>
            )}
          </div>
          {/* Progress bar */}
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4].map(s => (
              <div key={s} className="flex-1 flex flex-col items-center gap-1">
                <div className={`h-1.5 w-full rounded-full transition-all duration-500 ${
                  (isComplete || currentStep > s) ? 'bg-atlasPurple' : currentStep === s ? 'bg-atlasPurple/50' : 'bg-atlasBorder'
                }`} />
                <span className={`text-xs transition-all ${
                  (isComplete || currentStep >= s) ? 'text-atlasPurple' : 'text-atlasTextSecondary'
                }`}>{getStepLabel(s)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-3xl mx-auto space-y-4">
          {messages.map((msg, i) => (
            <div key={i} data-testid={`debrief-msg-${i}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-2xl px-5 py-3.5 ${
                msg.role === 'user'
                  ? 'bg-atlasPurple/15 text-white rounded-br-md'
                  : 'bg-atlasCard border border-atlasBorder text-white rounded-bl-md'
              }`}>
                {msg.role === 'atlas' && (
                  <div className="flex items-center gap-2 mb-1.5">
                    <Moon className="w-3.5 h-3.5 text-atlasPurple" />
                    <span className="text-xs text-atlasPurple font-medium">ATLAS</span>
                  </div>
                )}
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-atlasCard border border-atlasBorder rounded-2xl rounded-bl-md px-5 py-3.5">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 text-atlasPurple animate-spin" />
                  <span className="text-sm text-atlasTextSecondary">{t('debrief.thinking')}</span>
                </div>
              </div>
            </div>
          )}
          {isComplete && (
            <div className="flex justify-center pt-4">
              <div data-testid="debrief-complete-banner" className="flex items-center gap-2 px-4 py-2 rounded-full bg-atlasPurple/10 border border-atlasPurple/30">
                <CheckCircle2 className="w-4 h-4 text-atlasPurple" />
                <span className="text-sm text-atlasPurple">{t('debrief.completed')}</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      {!isComplete && (
        <div className="flex-shrink-0 border-t border-atlasBorder bg-atlasCard/80 backdrop-blur-lg px-4 py-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3">
              <textarea ref={inputRef} data-testid="debrief-input" value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
                placeholder={t('debrief.inputPlaceholder')} rows={1}
                className="flex-1 bg-atlasBg border border-atlasBorder rounded-xl px-4 py-3 text-white text-sm resize-none focus:outline-none focus:border-atlasPurple/50 placeholder:text-atlasTextSecondary" />
              <Button data-testid="debrief-send-btn" onClick={submitStep} disabled={!input.trim() || sending} className="bg-atlasPurple hover:bg-atlasPurple/80 text-white p-3 rounded-xl">
                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </Button>
            </div>
            {currentStep === 4 && (
              <button data-testid="debrief-nothing-btn" onClick={handleSkipStep4} className="mt-2 w-full text-center text-sm text-atlasTextSecondary hover:text-atlasPurple transition-colors py-2">
                {t('debrief.nothingToAdd')}
              </button>
            )}
          </div>
        </div>
      )}

      {isComplete && (
        <div className="flex-shrink-0 border-t border-atlasBorder bg-atlasCard/80 backdrop-blur-lg px-4 py-4">
          <div className="max-w-3xl mx-auto">
            <Button data-testid="debrief-back-dashboard-btn" onClick={() => navigate('/dashboard')} className="bg-atlasPurple hover:bg-atlasPurple/80 text-white w-full">
              {t('debrief.backToDashboard')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FridayDebrief;
